import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { useMoney } from '../../hooks/usePublicSettings'
import { accountApi, jobsApi, statusLabel, timeAgo } from '../../utils/accountApi'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'

const NOT_AVAILABLE = 'Not available yet'

function Row({ icon, title, subtitle, onClick, right, disabled }) {
  return (
    <Card hover={!disabled} onClick={disabled ? undefined : onClick}>
      <div className={`flex items-center justify-between ${disabled ? 'opacity-60' : ''}`} title={disabled ? NOT_AVAILABLE : undefined}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <div className="font-medium text-slate-900">{title}</div>
            <div className="text-sm text-slate-600">{subtitle}</div>
          </div>
        </div>
        {right ?? (disabled
          ? <span className="text-xs text-slate-400">{NOT_AVAILABLE}</span>
          : <span className="text-slate-400">→</span>)}
      </div>
    </Card>
  )
}

const EVENT_STYLE = {
  COMPLETED: ['bg-green-100', 'text-green-600', '✓'], APPROVED: ['bg-green-100', 'text-green-600', '✓'],
  CANCELLED: ['bg-red-100', 'text-red-600', '✕'], REQUESTED: ['bg-yellow-100', 'text-yellow-600', '📝']
}

export default function Profile() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const money = useMoney()
  const { showSuccess, showError } = useNotifications()
  const [data, setData] = useState(null)
  const [activity, setActivity] = useState([])
  const [error, setError] = useState(null)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ name: '', phone: '' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const [summary, events] = await Promise.all([accountApi.summary(), jobsApi.activity(5)])
      setData(summary)
      setActivity(events)
      setError(null)
    } catch (e) {
      setError(e.message)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const profile = data?.profile || user
  const stats = data?.stats
  const sub = data?.subscription
  const since = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
    : null

  const startEdit = () => {
    setForm({ name: profile?.name || '', phone: profile?.phone || '' })
    setEditing(true)
  }
  const saveProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await accountApi.updateProfile({ name: form.name.trim() || undefined, phone: form.phone.trim() || undefined })
      showSuccess('Account details saved')
      setEditing(false)
      await load()
    } catch (err) {
      showError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const yearly = sub?.billing_interval === 'year'
  const price = sub ? (yearly ? sub.price_yearly : sub.price_monthly) : null

  return (
    <PageLayout
      title="Profile"
      subtitle="Manage your account and preferences"
      onRefresh={load}
    >
      {error && (
        <Card><div className="text-sm text-red-600">Could not load your account: {error}</div></Card>
      )}

      {/* User Info */}
      <Card>
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center">
            <span className="text-xl font-semibold text-slate-600">
              {profile?.name?.charAt(0) || 'U'}
            </span>
          </div>
          <div>
            <div className="text-lg font-semibold text-slate-900">{profile?.name || 'User'}</div>
            <div className="text-sm text-slate-600">{profile?.email}</div>
            {since && <div className="text-xs text-slate-500 mt-1">Customer since {since}</div>}
          </div>
        </div>
      </Card>

      {/* Quick Stats */}
      <Card>
        <h3 className="font-semibold text-slate-900 mb-4">Quick Stats</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats ? stats.requests : '—'}</div>
            <div className="text-xs text-slate-600">Total Orders</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{stats ? stats.assets : '—'}</div>
            <div className="text-xs text-slate-600">Assets Tracked</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{stats?.avg_rating_given ?? '—'}</div>
            <div className="text-xs text-slate-600">Avg Rating Given</div>
          </div>
        </div>
      </Card>

      {/* Account Management */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Account Management</h3>
        <Row icon="🏠" title="Asset Tracker" subtitle="Manage your home appliances and equipment" onClick={() => navigate('/assets')} />
        <Row icon="📍" title="Address Book" subtitle="Manage your saved addresses and locations" onClick={() => navigate('/addresses')} />
        <Row icon="⚙️" title="Account Settings" subtitle="Update your name and phone number" onClick={editing ? undefined : startEdit} />
        {editing && (
          <Card>
            <form onSubmit={saveProfile} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} inputMode="tel"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900" />
              </div>
              <div className="text-xs text-slate-500">Email: {profile?.email} (cannot be changed here)</div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(false)} className="flex-1 py-2 px-3 bg-slate-100 text-slate-700 text-sm rounded-lg font-medium">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 py-2 px-3 bg-slate-900 text-white text-sm rounded-lg font-medium disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </Card>
        )}
        <Row icon="💳" title="Payment Methods"
          subtitle={stats ? `${stats.payment_methods} saved` : 'Manage payment options'}
          onClick={() => navigate('/payment-methods')} />
        <Row icon="❓" title="Help & Support" subtitle="Ask the assistant about your home or a booking" onClick={() => navigate('/chat')} />
      </div>

      {/* Preferences */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Preferences</h3>
        <Row icon="🔔" title="Notifications" subtitle="Push notifications, email alerts" disabled />
        <Row icon="🌙" title="Dark Mode" subtitle="Switch to dark theme" disabled />
      </div>

      {/* Recent Activity */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Recent Activity</h3>
        <Card>
          {activity.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-4">No activity yet. Your requests and jobs will show here.</div>
          ) : (
            <div className="space-y-4">
              {activity.map((e, i) => {
                const [bg, fg, icon] = EVENT_STYLE[e.status] || ['bg-blue-100', 'text-blue-600', '📅']
                return (
                  <div key={`${e.request_id}-${e.status}-${i}`} className={`flex items-center gap-3 ${e.job_id ? 'cursor-pointer' : ''}`}
                    onClick={() => e.job_id && navigate(`/jobs/${e.job_id}`)}>
                    <div className={`w-8 h-8 ${bg} rounded-full flex items-center justify-center`}>
                      <span className={`${fg} text-xs`}>{icon}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-900">{statusLabel(e.status)}</div>
                      <div className="text-xs text-slate-500">{e.title}</div>
                      <div className="text-xs text-slate-400">{timeAgo(e.at)}</div>
                    </div>
                    {e.amount != null && <div className="text-sm font-medium text-slate-900">{money(e.amount)}</div>}
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Subscription & Billing */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Subscription & Billing</h3>
        <Card>
          {!sub ? (
            <div className="text-sm text-slate-500">{data ? 'No subscription on this account.' : 'Loading…'}</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-medium text-slate-900">{sub.display_name}</div>
                  <div className="text-sm text-slate-600">{sub.description}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-slate-900">{money(price)}</div>
                  <div className="text-xs text-slate-500">per {yearly ? 'year' : 'month'}</div>
                </div>
              </div>
              {Array.isArray(sub.features) && sub.features.length > 0 && (
                <ul className="text-xs text-slate-600 mb-3 list-disc pl-4">
                  {sub.features.map(f => <li key={f}>{f}</li>)}
                </ul>
              )}
              <div className="text-xs text-slate-500 mb-3">
                Status: {sub.status}
                {sub.current_period_end && ` · ${sub.status === 'canceled' ? 'Ends' : 'Current period ends'} ${new Date(sub.current_period_end).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
              </div>
              <div className="flex gap-2">
                <button disabled title={NOT_AVAILABLE} className="flex-1 py-2 px-3 bg-slate-100 text-slate-700 text-sm rounded-lg font-medium opacity-50 cursor-not-allowed">Manage</button>
                <button disabled title={NOT_AVAILABLE} className="flex-1 py-2 px-3 bg-slate-900 text-white text-sm rounded-lg font-medium opacity-50 cursor-not-allowed">Upgrade</button>
              </div>
              <div className="text-xs text-slate-400 mt-2">Changing plans in the app is not available yet.</div>
            </>
          )}
        </Card>
      </div>

      {/* Security */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Security</h3>
        <Row icon="🔒" title="Change Password" subtitle="Update your account password" disabled />
        <Row icon="🛡️" title="Two-Factor Authentication" subtitle="Add extra security to your account" disabled />
        <Row icon="📱" title="Trusted Devices" subtitle="Manage your logged-in devices" disabled />
      </div>

      {/* Legal & Privacy */}
      <div className="space-y-3">
        <h3 className="font-semibold text-slate-900">Legal & Privacy</h3>
        <Row icon="📄" title="Terms of Service" subtitle="Read our terms and conditions" disabled />
        <Row icon="🔐" title="Privacy Policy" subtitle="How we handle your data" disabled />
        <Row icon="📊" title="Data Export" subtitle="Download your account data" disabled />
      </div>

      {/* Sign Out */}
      <Card>
        <button
          onClick={logout}
          className="w-full py-3 text-red-600 text-sm font-medium touch-manipulation"
        >
          Sign Out
        </button>
      </Card>
    </PageLayout>
  )
}
